import { threeDStudioStore } from '../index'

const SUBSCRIPTION_QUOTA_KEYS = [
  'studioAccessTier',
  'premiumFeatures',
  'monthlyGenerationsAvailable',
  'monthlyGenerationsUsed',
  'extraGenerationsAvailable',
  'extraGenerationsUsed',
  'subscriptionResetDate',
  'concurrencyCap',
]

// The counters are fractional now that a 2D image generation costs 0.1 credit, and Rails
// serialises an un-cast BigDecimal as the string "0.1e0". Admitting that string would put
// it straight into the usage progress bar, where used/total evaluates to NaN. The
// pageConfig path (useHydrateGenerationQuota) already type-checks these; this is the
// websocket path's equivalent.
const NUMERIC_QUOTA_KEYS = new Set([
  'monthlyGenerationsAvailable',
  'monthlyGenerationsUsed',
  'extraGenerationsAvailable',
  'extraGenerationsUsed',
  'concurrencyCap',
])

const isApplicableQuotaValue = (key, value) => (
  NUMERIC_QUOTA_KEYS.has(key) ? Number.isFinite(value) : true
)

const setSubscriptionState = (patch) => {
  threeDStudioStore.setState((state) => ({
    subscription: { ...state.subscription, ...patch },
  }))
}

/**
 * Merges a quota payload (from the generator-run channel) into the subscription slice
 * in a single setState. Only whitelisted keys with defined values are applied.
 * @param {object|undefined|null} quota
 */
export const applySubscriptionQuota = (quota) => {
  if (!quota) {
    return
  }

  const patch = {}
  SUBSCRIPTION_QUOTA_KEYS.forEach((key) => {
    if (quota[key] !== undefined && isApplicableQuotaValue(key, quota[key])) {
      patch[key] = quota[key]
    }
  })

  if (Object.keys(patch).length === 0) {
    return
  }

  setSubscriptionState(patch)
}

/**
 * Hydrates the subscription slice in a single setState.
 * - `overwrite` fields are always applied.
 * - `preserveIfSet` fields are only applied when the current value is null
 *   (so a previous hydration path is not clobbered).
 * @param {{ overwrite?: object, preserveIfSet?: object }} [patches]
 */
export const hydrateSubscription = ({ overwrite = {}, preserveIfSet = {} } = {}) => {
  threeDStudioStore.setState((state) => {
    const current = state.subscription
    const preserved = {}
    Object.entries(preserveIfSet).forEach(([key, value]) => {
      if (current[key] === null) {
        preserved[key] = value
      }
    })

    return {
      subscription: { ...current, ...overwrite, ...preserved },
    }
  })
}

/**
 * Sets the current AI Studio access tier, and the premium entitlement when the caller knows it.
 * The two are written together because a resolved tier next to a null premiumFeatures reads as
 * "locked" to the DCC bridge, export and private-mode gates.
 * @param {'not_subscribed'|'eligible_for_trial'|'in_free_beta'|'subscribed'|null} studioAccessTier
 * @param {boolean|undefined} premiumFeatures - omitted leaves the current value untouched
 */
export const setStudioAccessTier = (studioAccessTier, premiumFeatures) => {
  setSubscriptionState(
    typeof premiumFeatures === 'boolean'
      ? { studioAccessTier, premiumFeatures }
      : { studioAccessTier },
  )
}

/**
 * Sets the monthly generations available quota
 * @param {number|null} monthlyGenerationsAvailable - Total monthly generations granted to the user
 */
export const setMonthlyGenerationsAvailable = (monthlyGenerationsAvailable) => {
  setSubscriptionState({ monthlyGenerationsAvailable })
}

/**
 * Sets the monthly generations used count
 * @param {number|null} monthlyGenerationsUsed - Count of monthly generations already consumed
 */
export const setMonthlyGenerationsUsed = (monthlyGenerationsUsed) => {
  setSubscriptionState({ monthlyGenerationsUsed })
}

/**
 * Sets the extra generations available quota
 * @param {number|null} extraGenerationsAvailable - Additional generations granted beyond the monthly quota
 */
export const setExtraGenerationsAvailable = (extraGenerationsAvailable) => {
  setSubscriptionState({ extraGenerationsAvailable })
}

/**
 * Sets the extra generations used count
 * @param {number|null} extraGenerationsUsed - Count of extra generations already consumed
 */
export const setExtraGenerationsUsed = (extraGenerationsUsed) => {
  setSubscriptionState({ extraGenerationsUsed })
}

/**
 * Sets the AI Studio subscription current price (pre-formatted string, e.g. "$9.99")
 * @param {string|null} subscriptionPrice
 */
export const setSubscriptionPrice = (subscriptionPrice) => {
  setSubscriptionState({ subscriptionPrice })
}

/**
 * Sets the AI Studio subscription original (pre-discount) price
 * @param {string|null} subscriptionOriginalPrice
 */
export const setSubscriptionOriginalPrice = (subscriptionOriginalPrice) => {
  setSubscriptionState({ subscriptionOriginalPrice })
}

/**
 * Sets the pre-formatted reset date shown in the usage modal (e.g. "May 12")
 * @param {string|null} subscriptionResetDate
 */
export const setSubscriptionResetDate = (subscriptionResetDate) => {
  setSubscriptionState({ subscriptionResetDate })
}

/**
 * Sets the pre-formatted extra-generations pack price (e.g. "$9.99")
 * @param {string|null} extraGenerationsPrice
 */
export const setExtraGenerationsPrice = (extraGenerationsPrice) => {
  setSubscriptionState({ extraGenerationsPrice })
}

/**
 * Sets the monthly generations included in the AI Studio subscription plan
 * @param {number|null} subscriptionMonthlyGenerations
 */
export const setSubscriptionMonthlyGenerations = (subscriptionMonthlyGenerations) => {
  setSubscriptionState({ subscriptionMonthlyGenerations })
}

/**
 * Sets the monthly model downloads included in the AI Studio subscription plan
 * @param {number|null} subscriptionMonthlyModels
 */
export const setSubscriptionMonthlyModels = (subscriptionMonthlyModels) => {
  setSubscriptionState({ subscriptionMonthlyModels })
}

/**
 * Sets the absolute URL of the subscription payment form. The route is on the
 * `subscriptions` subdomain in production, so links from www must use the
 * server-resolved URL instead of a bare path.
 * @param {string|null} subscriptionPaymentFormUrl
 */
export const setSubscriptionPaymentFormUrl = (subscriptionPaymentFormUrl) => {
  setSubscriptionState({ subscriptionPaymentFormUrl })
}
